import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Calendar, Clock, User, Tag, Repeat, MessageSquare, Trash2, Send, Save, Copy, AlarmClock, Paperclip, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_DOTS = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-slate-400' };

const TaskDetailPanel = ({ task, users, onClose, onChanged, onDelete, onWhatsapp, onDuplicate, onSnooze, dark = false }) => {
  const [draft, setDraft] = useState(task);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => { setDraft(task); }, [task?.id]); // eslint-disable-line

  if (!task) return null;

  const save = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/todos/${task.id}/edit`, patch, { headers: getAuthHeaders() });
      setDraft(data);
      onChanged && onChanged(data);
    } catch {
      toast.error('Failed to save');
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await axios.post(`${API}/todos/${task.id}/notes`, { text: newNote }, { headers: getAuthHeaders() });
      setDraft(data);
      onChanged && onChanged(data);
      setNewNote('');
    } catch {
      toast.error('Failed to add note');
    }
  };

  const addTag = () => {
    const t = (newTag || '').trim();
    if (!t) return;
    const next = Array.from(new Set([...(draft.tags || []), t]));
    save({ tags: next });
    setNewTag('');
  };

  const removeTag = (t) => {
    save({ tags: (draft.tags || []).filter(x => x !== t) });
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data } = await axios.post(`${API}/todos/${task.id}/attachments`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setDraft(data);
      onChanged && onChanged(data);
      toast.success('Attachment uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await axios.get(`${API}/todos/${task.id}/attachments/${att.name}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.original_name || att.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error('Download failed');
    }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.original_name}"?`)) return;
    try {
      await axios.delete(`${API}/todos/${task.id}/attachments/${att.name}`, { headers: getAuthHeaders() });
      const next = { ...draft, attachments: (draft.attachments || []).filter(a => a.name !== att.name) };
      setDraft(next);
      onChanged && onChanged(next);
    } catch {
      toast.error('Remove failed');
    }
  };

  const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`;

  const subtle = dark ? 'text-slate-400' : 'text-slate-500';
  const labelCls = `text-[11px] uppercase tracking-wide font-semibold ${subtle}`;
  const inputBg = dark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200';

  return (
    <aside className={`flex flex-col h-full border-l ${dark ? 'bg-slate-900 border-slate-800 text-slate-100' : 'bg-white border-slate-200'} w-[380px] flex-shrink-0`} data-testid="task-detail-panel">
      <div className={`flex items-center justify-between px-5 py-4 border-b ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${PRIORITY_DOTS[draft.priority] || PRIORITY_DOTS.medium}`} />
          <div className="text-sm font-semibold truncate">Task details</div>
        </div>
        <button onClick={onClose} className={`p-1.5 rounded-md hover:bg-slate-100 ${dark ? 'hover:bg-slate-800' : ''}`} data-testid="task-detail-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Title */}
        <div className="space-y-1.5">
          <Label className={labelCls}>Title</Label>
          <Input
            value={draft.title || ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            onBlur={() => draft.title !== task.title && save({ title: draft.title })}
            className={`${inputBg} text-base font-semibold`}
            data-testid="task-detail-title"
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label className={labelCls}>Description</Label>
          <Textarea
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            onBlur={() => (draft.description || '') !== (task.description || '') && save({ description: draft.description })}
            rows={3}
            className={inputBg}
            placeholder="Add details..."
          />
        </div>

        {/* Assignee + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}><User className="h-3 w-3 inline mr-1" />Assignee</Label>
            <Select value={draft.assigned_to} onValueChange={(v) => save({ assigned_to: v })}>
              <SelectTrigger className={inputBg}><SelectValue /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Priority</Label>
            <Select value={draft.priority || 'medium'} onValueChange={(v) => save({ priority: v })}>
              <SelectTrigger className={inputBg}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Date + Time */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}><Calendar className="h-3 w-3 inline mr-1" />Due date</Label>
            <Input type="date" value={draft.due_date || ''}
              onChange={(e) => save({ due_date: e.target.value })}
              className={inputBg} data-testid="task-detail-due-date" />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}><Clock className="h-3 w-3 inline mr-1" />Time</Label>
            <Input type="time" value={draft.due_time || ''}
              onChange={(e) => save({ due_time: e.target.value })}
              className={inputBg} />
          </div>
        </div>

        {/* Repeat + Progress */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}><Repeat className="h-3 w-3 inline mr-1" />Repeat</Label>
            <Select value={draft.repeat || 'none'} onValueChange={(v) => save({ repeat: v })}>
              <SelectTrigger className={inputBg}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Progress {draft.progress ?? 0}%</Label>
            <input type="range" min="0" max="100" step="5"
              value={draft.progress ?? 0}
              onChange={(e) => setDraft({ ...draft, progress: Number(e.target.value) })}
              onMouseUp={() => save({ progress: draft.progress })}
              onTouchEnd={() => save({ progress: draft.progress })}
              className="w-full accent-blue-600 h-9"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-1.5">
          <Label className={labelCls}><Tag className="h-3 w-3 inline mr-1" />Tags</Label>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {(draft.tags || []).map(t => (
              <span key={t} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${dark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                #{t}
                <button onClick={() => removeTag(t)} className="hover:text-rose-500">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newTag} onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add tag" className={`${inputBg} h-8 text-sm`} />
            <Button onClick={addTag} size="sm" variant="outline">Add</Button>
          </div>
        </div>

        {/* Attachments */}
        <div className="space-y-1.5">
          <Label className={labelCls}><Paperclip className="h-3 w-3 inline mr-1" />Attachments</Label>
          <div className="space-y-1.5">
            {(draft.attachments || []).length === 0 && <div className={`text-xs ${subtle}`}>No files yet.</div>}
            {(draft.attachments || []).map((a) => (
              <div key={a.name} className={`flex items-center gap-2 p-2 rounded-md border ${dark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50'}`}>
                <Paperclip className="h-3.5 w-3.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{a.original_name}</div>
                  <div className={`text-[10px] ${subtle}`}>{fmtBytes(a.size)} · {a.uploaded_by}</div>
                </div>
                <button onClick={() => downloadAttachment(a)} title="Download"
                  className={`p-1.5 rounded-md ${dark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                  data-testid={`attachment-download-${a.name}`}>
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => removeAttachment(a)} title="Remove"
                  className={`p-1.5 rounded-md text-rose-500 ${dark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <input ref={fileInputRef} type="file" className="hidden" onChange={onUpload} data-testid="attachment-input" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="attachment-upload-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" />Upload file (max 5MB)
          </Button>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <Label className={labelCls}><MessageSquare className="h-3 w-3 inline mr-1" />Notes / Comments</Label>
          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {(draft.notes || []).length === 0 && <div className={`text-xs ${subtle}`}>No notes yet.</div>}
            {(draft.notes || []).map((n, i) => (
              <div key={i} className={`text-xs p-2 rounded-md border ${dark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
                <div>{n.text}</div>
                <div className={`text-[10px] mt-1 ${subtle}`}>{n.by} · {n.at ? new Date(n.at).toLocaleString('en-IN') : ''}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newNote} onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNote())}
              placeholder="Add a note" className={`${inputBg} h-8 text-sm`} data-testid="task-note-input" />
            <Button onClick={addNote} size="sm" data-testid="task-note-add"><Save className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`flex flex-wrap gap-2 px-5 py-3 border-t ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
        <Button variant="outline" size="sm" onClick={() => onSnooze(task)} data-testid="task-snooze-btn">
          <AlarmClock className="h-3.5 w-3.5 mr-1" />Snooze
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDuplicate(task)} data-testid="task-duplicate-btn">
          <Copy className="h-3.5 w-3.5 mr-1" />Duplicate
        </Button>
        <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300" onClick={() => onWhatsapp(task)} data-testid="task-whatsapp-btn">
          <Send className="h-3.5 w-3.5 mr-1" />WhatsApp
        </Button>
        <Button variant="outline" size="sm" className="text-rose-700 border-rose-300 ml-auto" onClick={() => onDelete(task)} data-testid="task-delete-btn">
          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
        </Button>
      </div>
    </aside>
  );
};

export default TaskDetailPanel;
